/**
 * The rules the money runs on — §6.9's "payment settings".
 *
 * Three rules and their exceptions: when a late fee is charged and how much,
 * when the reminders go out, and whether cards carry a surcharge. They are
 * company-wide, which is why this is `full` and why the waivers are on the
 * same screen: a rule and the list of times somebody overrode it belong
 * together, or the rule looks stricter than it is.
 *
 * The surcharge is deliberately half-built, and says so. §6.9: the feature
 * ships with `enabled = false` and stays behind a Rowted-admin flag until the
 * per-state legal review is done — surcharging a card is illegal in some
 * states and capped in others. A switch that silently did nothing would be
 * worse than a switch that is not there, so this one explains itself.
 */
import { useCallback, useEffect, useState } from 'react'
import { SlideOver } from './overlays.js'
import { Button, Card, SectionLabel, Switch, Tag } from './primitives.js'
import { Field } from './fields.js'
import { formatDollars, formatRelative } from '../lib/format.js'
import { request } from '../lib/api.js'

/** Rates arrive as percentages: the route converts at the edge (§F.1). */
interface Settings {
  lateFeeAfterDays: number
  lateFeePct: number
  lateFeeMinCents: number
  lateFeeWaiveRole: string
  surchargeUnlocked: boolean
  surchargeEnabled: boolean
  surchargeRate: number
  surchargeCostRate: number
  surchargeShowOnPay: boolean
  reminderPreDays: number
  reminderEnabled: boolean
  reminderHandoffDays: number
}

interface Waiver {
  id: string
  reference: string
  amount: string
  reason: string
  kind: string
  issuedAt: string
  byName: string | null
}

const KIND_LABEL: Record<string, string> = {
  waived_late_fee: 'Waived fee',
  goodwill: 'Goodwill',
  service_credit: 'Service credit',
}

export function PaymentSettings({
  open, onClose, canEdit,
}: {
  open: boolean
  onClose: () => void
  canEdit: boolean
}) {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [waivers, setWaivers] = useState<Waiver[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    try {
      const payload = await request<{ settings: Settings; waivers: Waiver[] }>(
        '/invoices/settings/payment')
      setSettings(payload.settings)
      setWaivers(payload.waivers)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Those could not be loaded.')
    }
  }, [])

  useEffect(() => { if (open) void load() }, [open, load])

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings((current) => (current ? { ...current, [key]: value } : current))

  async function save() {
    if (!settings) return
    setBusy(true)
    setError(null)
    try {
      const payload = await request<{ settings: Settings; waivers: Waiver[] }>(
        '/invoices/settings/payment', {
          method: 'PUT',
          body: {
            lateFeeAfterDays: settings.lateFeeAfterDays,
            lateFeePct: settings.lateFeePct,
            lateFeeMin: settings.lateFeeMinCents / 100,
            surchargeEnabled: settings.surchargeEnabled,
            surchargeRate: settings.surchargeRate,
            surchargeShowOnPay: settings.surchargeShowOnPay,
            reminderPreDays: settings.reminderPreDays,
            reminderEnabled: settings.reminderEnabled,
            reminderHandoffDays: settings.reminderHandoffDays,
          },
        })
      setSettings(payload.settings)
      setWaivers(payload.waivers)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2500)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Those could not be saved.')
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Payment settings"
      width={460}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          {canEdit && (
            <Button variant="primary" disabled={busy || !settings} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          )}
        </>
      }
    >
      {!settings
        ? <p className="m-0 text-cell text-text-muted">{error ?? 'Loading…'}</p>
        : (
          <div className="flex flex-col gap-4">
            {error && (
              <p className="m-0 rounded-card border border-danger bg-danger-bg p-2 text-cell text-danger">
                {error}
              </p>
            )}
            {saved && <p className="m-0 text-sub text-accent-dark">Saved.</p>}

            <section className="flex flex-col gap-2.5">
              <SectionLabel>Late fees</SectionLabel>
              <div className="grid grid-cols-3 gap-2">
                <Field
                  label="After (days)"
                  value={String(settings.lateFeeAfterDays)}
                  onChange={(value) => set('lateFeeAfterDays', Number(value) || 0)}
                />
                <Field
                  label="Rate (%)"
                  value={String(settings.lateFeePct)}
                  onChange={(value) => set('lateFeePct', Number(value) || 0)}
                />
                <Field
                  label="Minimum"
                  value={(settings.lateFeeMinCents / 100).toFixed(2)}
                  onChange={(value) =>
                    set('lateFeeMinCents', Math.round((Number(value) || 0) * 100))}
                />
              </div>
              <p className="m-0 text-sub text-text-muted">
                Charged as its own invoice, not as a line on the one it is for — that
                one was issued and is frozen. Waived by {settings.lateFeeWaiveRole
                  .replace(/_/g, ' ')} and above, with a reason.
              </p>
            </section>

            <section className="flex flex-col gap-2.5 border-t border-line-soft pt-3">
              <SectionLabel>Reminders</SectionLabel>
              <Switch
                checked={settings.reminderEnabled}
                onChange={(value) => set('reminderEnabled', value)}
                label="Chase unpaid invoices"
              />
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="First one, days before due"
                  value={String(settings.reminderPreDays)}
                  onChange={(value) => set('reminderPreDays', Number(value) || 0)}
                />
                <Field
                  label="Hand off after (days)"
                  value={String(settings.reminderHandoffDays)}
                  onChange={(value) => set('reminderHandoffDays', Number(value) || 0)}
                />
              </div>
              <p className="m-0 text-sub text-text-muted">
                Then on the due date, +7, +14 with the late fee, and +30 to a person.
                A disputed invoice drops out of the sequence until the dispute is settled.
              </p>
            </section>

            <section className="flex flex-col gap-2.5 border-t border-line-soft pt-3">
              <div className="flex items-center gap-2">
                <SectionLabel>Card surcharge</SectionLabel>
                {!settings.surchargeUnlocked && <Tag tone="warn">Not available yet</Tag>}
              </div>

              {settings.surchargeUnlocked
                ? (
                  <>
                    <Switch
                      checked={settings.surchargeEnabled}
                      onChange={(value) => set('surchargeEnabled', value)}
                      label="Add a surcharge to card payments"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Field
                        label="Rate (%)"
                        value={String(settings.surchargeRate)}
                        onChange={(value) => set('surchargeRate', Number(value) || 0)}
                      />
                      {/* Read-only: it is what the card network charges, not
                          a number anybody here gets to pick. §6.9 caps the
                          surcharge at cost, and the database enforces it. */}
                      <Field
                        label="What it costs us (%)"
                        value={String(settings.surchargeCostRate)}
                        onChange={() => {}}
                        hint="Set by the card network. The surcharge cannot exceed it."
                      />
                    </div>
                    <Switch
                      checked={settings.surchargeShowOnPay}
                      onChange={(value) => set('surchargeShowOnPay', value)}
                      label="Show it on the pay page"
                    />
                  </>
                )
                : (
                  <p className="m-0 text-sub text-text-muted">
                    Surcharging a card is illegal in some states and capped in others,
                    so it is off until the state-by-state review is done. Rowted turns
                    it on per company. Nothing is being added to any card today.
                  </p>
                )}
            </section>

            <section className="flex flex-col gap-2 border-t border-line-soft pt-3">
              <SectionLabel>Waivers and credits</SectionLabel>
              {waivers.length === 0
                ? (
                  <p className="m-0 text-sub text-text-muted">
                    Nothing has been waived or credited. When it is, it is here with
                    who did it and why.
                  </p>
                )
                : (
                  <Card className="overflow-hidden">
                    <ul className="m-0 list-none p-0">
                      {waivers.map((waiver) => (
                        <li key={waiver.id}
                          className="border-b border-line-soft px-2.5 py-2 last:border-b-0">
                          <div className="flex items-baseline gap-2">
                            <Tag>{KIND_LABEL[waiver.kind] ?? waiver.kind}</Tag>
                            <span className="tnum text-cell font-semibold text-text">
                              {formatDollars(Number(waiver.amount))}
                            </span>
                            <span className="ml-auto text-sub text-text-faint">
                              {waiver.byName ?? 'Somebody'} · {formatRelative(waiver.issuedAt)}
                            </span>
                          </div>
                          <p className="m-0 mt-0.5 text-sub text-text-muted">{waiver.reason}</p>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
            </section>
          </div>
        )}
    </SlideOver>
  )
}
